import React, { useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { MdsIcon } from "@/design-system/components/icon";
import { AppId, AppSelectionStepProps } from "./ImportWizard.types";
import { NotesApps } from "./ImportWizard.config";
import {
  FooterButton,
  FooterButtons,
  FooterContainer,
  StepTitle,
  WizardContentWrapper,
} from "@/components/onboarding/components/ImportWizard/StyledComponents";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { logger } from "@/modules/logger";

export const AppSelectionStep: React.FC<AppSelectionStepProps> = ({
  selectedAppId,
  customAppName,
  onAppSelect,
  onCustomAppNameChange,
  onNavToStep,
}) => {
  const otherInputRef = useRef<HTMLInputElement>(null);
  const isContinueButtonEnabled =
    selectedAppId === "other" ? customAppName.trim().length >= 1 : !!selectedAppId;

  const handleAppSelect = (appId: AppId) => {
    onAppSelect(appId);
  };

  const handleContinue = () => {
    if (!selectedAppId) {
      logger.error({ message: "handleContinue in AppSelectionStep called with no selectedAppId" });
      return;
    }

    trackEvent(TrackedEvent.ImportWizardAppSelect, {
      appId: selectedAppId,
      otherAppName:
        selectedAppId === "other"
          ? customAppName.toLowerCase().trim().replace(/\s+/g, " ") // lowercase and normalize whitespace
          : undefined,
    });

    switch (selectedAppId) {
      case "apple-notes":
        onNavToStep("apple-notes-1");
        break;
      case "obsidian":
        onNavToStep("obsidian-1");
        break;
      case "other":
        onNavToStep("other-apps-1");
        break;
      default:
        onNavToStep("skip-confirmation");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      if (isContinueButtonEnabled) {
        handleContinue();
      }
    }
  };

  // Auto-focus the input when "Other" is selected
  useEffect(() => {
    if (selectedAppId === "other" && otherInputRef.current) {
      otherInputRef.current.focus();
    }
  }, [selectedAppId]);

  return (
    <WizardContentWrapper onKeyDown={handleKeyDown}>
      <StepTitle>What notes app do you currently use?</StepTitle>

      <AppOptionsSection>
        {NotesApps.map(app => (
          <AppOption
            key={app.id}
            isSelected={selectedAppId === app.id}
            onClick={() => handleAppSelect(app.id)}
            isOtherOption={app.id === "other"}
          >
            {(app.iconImage || app.iconKind) && (
              <AppIconWrapper>
                {app.iconKind && <MdsIcon kind={app.iconKind} />}
                {app.iconImage && <img src={app.iconImage} alt={app.title} />}
              </AppIconWrapper>
            )}

            {app.id === "other" && selectedAppId === "other" ? (
              <OtherInput
                ref={otherInputRef}
                placeholder="Enter your notes app name"
                value={customAppName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  onCustomAppNameChange(e.target.value)
                }
                onClick={(e: React.MouseEvent) => e.stopPropagation()} // Prevent triggering the button click
              />
            ) : (
              app.title
            )}
          </AppOption>
        ))}
      </AppOptionsSection>
      <FooterContainer>
        <FooterButtons>
          <span>&nbsp;</span>
          <FooterButton
            onClick={handleContinue}
            color="contrast"
            isDisabled={!isContinueButtonEnabled}
          >
            Continue
          </FooterButton>
        </FooterButtons>
      </FooterContainer>
    </WizardContentWrapper>
  );
};

const AppOptionsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const AppOption = styled.button<{ isSelected: boolean; isOtherOption?: boolean }>`
  display: flex;
  align-items: center;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  font-size: ${({ theme }) => theme.fontSizes.medium};
  gap: ${({ theme }) => theme.spacing.xxs};
  padding: ${({ theme }) => theme.spacing.smd} ${({ theme }) => theme.spacing.md};
  background: ${({ isSelected, theme }) =>
    isSelected ? theme.colors.mblue.x50 : theme.colors.grey.x0};
  border: 1px solid
    ${({ isSelected, theme }) => (isSelected ? theme.colors.mblue.x200 : theme.colors.grey.x100)};
  border-radius: ${({ theme }) => theme.borderRadius.mediumLarge + "px"};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  min-height: 60px;
  color: ${({ theme, isSelected }) =>
    isSelected ? theme.colors.mblue.x500 : theme.colors.grey.x600};

  &:hover {
    background: ${({ isSelected, theme }) =>
      isSelected ? theme.colors.mblue.x50 : theme.colors.grey.x25};
    border-color: ${({ isSelected, theme }) =>
      isSelected ? theme.colors.mblue.x200 : theme.colors.grey.x200};
  }
`;

const AppIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => theme.spacing.xxl};
  height: ${({ theme }) => theme.spacing.xxl};

  & img {
    width: ${({ theme }) => theme.spacing.xl};
    height: ${({ theme }) => theme.spacing.xl};
  }
`;

const OtherInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.grey.x600};
  outline: none;

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey.x400};
  }
`;
